import { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2 } from "lucide-react";
import { cn } from "@/lib/cn";
import type { ProsthesisStatus } from "@bookdental/schemas";
import { ProductionKanbanCard } from "./ProductionKanbanCard";
import type { ServiceRow } from "./production-types";
import { SeletorDeCorDoEstagio } from "./SeletorDeCorDoEstagio";
import type { PrazoDoLaboratorio } from "@bookdental/schemas";

const COMPLETION_BEHAVIOR_LABEL: Record<string, string> = {
  "": "Nenhum",
  CONCLUIDO: "Destino ao Concluir",
  CANCELADO: "Destino ao Cancelar",
};

const defaultDeleteConfirm = (label: string) =>
  `Excluir o estágio "${label}"? Os serviços nele voltam pra "Sem estágio".`;

type StageMeta = { id: string; label: string; colorHex: string | null; completionBehavior?: ProsthesisStatus | null };

/** Coluna do board de Estágios/Modelo — `stage: null` é a coluna fixa "sem etapa" (sempre primeira,
 *  não editável/deletável/arrastável). As demais são linhas de verdade (`ProductionStage` ou
 *  `ModelStage`), com nome/cor/ordem editáveis direto no cabeçalho. `onCompletionBehavior` só se
 *  aplica a Estágio de produção — quando omitido (board de Modelo), o select correspondente nem
 *  renderiza, já que progresso de modelo nunca influencia o status do serviço. Direção do
 *  `completionBehavior`: marcar o serviço com aquele status (Concluído/Cancelado, via "Finalizar
 *  Serviço") move ele pra este estágio automaticamente — entrar manualmente no estágio (arrastar o
 *  card) nunca muda o status, só o contrário. */
export function StageColumn({
  stage,
  rows,
  prazo,
  noneLabel,
  configureAriaLabel = "Configurar estágio",
  deleteConfirmMessage = defaultDeleteConfirm,
  locked = false,
  podeArrastarCards = true,
  onOpenService,
  onRename,
  onColor,
  onCompletionBehavior,
  onDelete,
}: {
  stage: StageMeta | null;
  rows: ServiceRow[];
  prazo: PrazoDoLaboratorio;
  noneLabel: string;
  configureAriaLabel?: string;
  deleteConfirmMessage?: (label: string) => string;
  /** Personalização da coluna (arrastar/renomear/colorir/excluir) travada — hoje sempre `false`
   *  (ver `useCustomizationLocked`). A coluna continua recebendo cards normalmente. */
  locked?: boolean;
  /** Mover card entre estágios é trabalho de produção, não personalização — quem só tem
   *  `production.view` continua abrindo o card, mas não arrasta. */
  podeArrastarCards?: boolean;
  onOpenService?: (row: ServiceRow) => void;
  onRename?: (label: string) => void;
  onColor?: (hex: string) => void;
  onCompletionBehavior?: (behavior: ProsthesisStatus | null) => void;
  onDelete?: () => void;
}) {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: stage?.id ?? "unstaged" });
  const sortable = useSortable({
    id: `col:${stage?.id}`,
    data: { type: "column" },
    disabled: !stage || locked,
  });
  const [showSettings, setShowSettings] = useState(false);
  const color = stage?.colorHex ?? "#a1a1aa";

  const wrapperStyle = stage
    ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }
    : undefined;

  return (
    <div
      ref={stage ? sortable.setNodeRef : undefined}
      style={wrapperStyle}
      className={cn("flex w-72 shrink-0 flex-col gap-2", sortable.isDragging && "z-10 opacity-90")}
    >
      <div className="flex items-center gap-1.5 px-1">
        {stage && !locked ? (
          <button
            type="button"
            {...sortable.attributes}
            {...sortable.listeners}
            className="-m-2 flex h-11 w-11 shrink-0 cursor-grab touch-none items-center justify-center text-zinc-400 hover:text-zinc-600 active:cursor-grabbing md:m-0 md:h-3.5 md:w-3.5 dark:text-muted-foreground/50 dark:hover:text-foreground"
            aria-label={`Arrastar pra reordenar ${stage.label}`}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="h-3.5 w-3.5 shrink-0" />
        )}

        {stage && !locked ? (
          /* A bolinha de cor continua com 10px; quem cresce é a área de toque em volta dela.
             Pintar o botão inteiro viraria um círculo enorme no lugar do indicador. */
          <button
            type="button"
            onClick={() => setShowSettings((v) => !v)}
            className="-m-2 flex h-11 w-11 shrink-0 items-center justify-center md:m-0 md:h-2.5 md:w-2.5"
            aria-label={configureAriaLabel}
          >
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
          </button>
        ) : (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: stage ? color : undefined }}
          />
        )}

        {stage && !locked ? (
          <input
            key={stage.id + stage.label}
            defaultValue={stage.label}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value.length >= 2 && value !== stage.label) onRename?.(value);
            }}
            className="h-11 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 text-base font-bold uppercase tracking-wide text-zinc-700 hover:border-zinc-200 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 md:h-7 md:text-sm dark:text-foreground/80 dark:hover:border-border"
          />
        ) : (
          <h3 className="flex-1 truncate text-sm font-bold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
            {stage ? stage.label : noneLabel}
          </h3>
        )}

        <span
          className="shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-semibold"
          style={{ backgroundColor: `${color}1a`, color }}
        >
          {rows.length}
        </span>

        {stage && !locked && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm(deleteConfirmMessage(stage.label))) {
                onDelete?.();
              }
            }}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600 md:h-6 md:w-6 dark:hover:bg-red-950/30 dark:hover:text-red-400"
            aria-label={`Excluir ${stage.label}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {stage && showSettings && (
        <div className="flex flex-col gap-1.5 px-1">
          <SeletorDeCorDoEstagio
            valor={stage.colorHex}
            onChange={(hex) => onColor?.(hex)}
            previa={stage.label}
          />
          {onCompletionBehavior && (
            <select
              value={stage.completionBehavior ?? ""}
              onChange={(e) => onCompletionBehavior((e.target.value || null) as ProsthesisStatus | null)}
              className="h-7 rounded-md border border-zinc-200 bg-white px-1.5 text-[11px] text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
            >
              {Object.entries(COMPLETION_BEHAVIOR_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div
        ref={setDropRef}
        className={cn(
          "flex min-h-[80px] flex-1 flex-col gap-2 rounded-xl p-2",
          isOver
            ? "border-2 border-dashed border-brand-400 bg-brand-50/50 dark:bg-primary/5"
            : "border border-zinc-100 bg-zinc-50/60 dark:border-border/60 dark:bg-background/30",
        )}
      >
        {rows.map((row) => (
          <ProductionKanbanCard
            key={row.service.id}
            row={row}
            prazo={prazo}
            arrastavel={podeArrastarCards}
            onOpenService={onOpenService}
          />
        ))}
      </div>
    </div>
  );
}
